import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash/fp";

import useBillingCustomer from "@app/analytics/account/Billing/hooks/useBillingCustomer";
import usePaymentMethod from "@app/analytics/account/Billing/hooks/usePaymentMethod";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import apiClient from "@app/common/api/apiClient";
import {
  InitiateSubscriptionCheckoutRequestBody,
  InitiateCreditCheckoutRequestBody,
  BillingCustomer,
  PaymentMethod,
  CheckoutBillingInfo,
} from "@app/common/types";
import logger from "@app/utils/logger";

import { getQueryKey as getBillingCustomerQueryKey } from "./useBillingCustomer";
import { getQueryKey as getPaymentMethodQueryKey } from "./usePaymentMethod";

type InitiateSubscriptionCheckoutMutationBody = Omit<
  InitiateSubscriptionCheckoutRequestBody,
  "billingAccountId"
>;

type InitiateCreditCheckoutMutationBody = Omit<
  InitiateCreditCheckoutRequestBody,
  "billingAccountId"
>;

const getCheckoutBillingInfo = (
  billingCustomer: BillingCustomer,
  paymentMethod?: PaymentMethod
): CheckoutBillingInfo => {
  const hasPaymentMethodAddress = !isEmpty(
    paymentMethod?.billingDetails?.address
  );

  const checkoutBillingInfo: CheckoutBillingInfo = hasPaymentMethodAddress
    ? {
        name: paymentMethod?.billingDetails.name,
        company: billingCustomer?.company,
        address: paymentMethod?.billingDetails.address,
      }
    : {
        name: billingCustomer?.name,
        company: billingCustomer?.company,
        address: billingCustomer?.address,
      };
  return checkoutBillingInfo;
};

const useCheckout = () => {
  const queryClient = useQueryClient();

  const { billingAccount } = useBillingContext();
  const { billingCustomer, isLoading: billingCustomerIsLoading } =
    useBillingCustomer();
  const { paymentMethod, paymentMethodIsLoading } = usePaymentMethod();

  const billingCustomerQueryKey = getBillingCustomerQueryKey(
    billingAccount?.userId
  );
  const paymentMethodQueryKey = getPaymentMethodQueryKey(billingAccount?.id);

  const isCheckoutBillingInfoLoading =
    billingCustomerIsLoading || paymentMethodIsLoading;
  const checkoutBillingInfo =
    !isCheckoutBillingInfoLoading && billingCustomer
      ? getCheckoutBillingInfo(billingCustomer, paymentMethod)
      : null;

  const initiateSubscriptionCheckoutMutation = useMutation({
    mutationFn: async (body: InitiateSubscriptionCheckoutMutationBody) => {
      if (!billingAccount?.id) throw new Error("No billing account id");

      try {
        await apiClient.billing.patchCustomer({
          id: billingAccount.id,
          company: body.billingInfo?.company,
        });
      } catch (e) {
        Sentry.captureException(e);
      }

      return apiClient.billing.initiateSubscriptionCheckout({
        ...body,
        billingAccountId: billingAccount.id,
      });
    },
    onSettled: async () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: paymentMethodQueryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: billingCustomerQueryKey,
        }),
      ]);
    },
  });

  const initiateUploadCreditCheckoutMutation = useMutation({
    mutationFn: async (body: InitiateCreditCheckoutMutationBody) => {
      if (!billingAccount?.id) throw new Error("No billing account id");

      try {
        await apiClient.billing.patchCustomer({
          id: billingAccount.id,
          company: body.billingInfo?.company,
        });
      } catch (e) {
        Sentry.captureException(e);
      }

      return apiClient.billing.initiateUploadCreditsCheckout({
        ...body,
        billingAccountId: billingAccount.id,
      });
    },
    onSettled: async () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: paymentMethodQueryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: billingCustomerQueryKey,
        }),
      ]);
    },
  });

  const cancelUploadCreditsCheckoutMutation = useMutation({
    mutationFn: async () => {
      if (!billingAccount?.id) {
        logger.debug("No billing account id");
        return;
      }
      return apiClient.billing.cancelCreditsCheckout(billingAccount.id);
    },
  });

  return {
    checkoutBillingInfo,
    isCheckoutBillingInfoLoading,
    cancelUploadCreditsCheckout: cancelUploadCreditsCheckoutMutation,
    initiateSubscriptionCheckout: initiateSubscriptionCheckoutMutation,
    initiateUploadCreditCheckout: initiateUploadCreditCheckoutMutation,
  };
};

export default useCheckout;
